import React from "react";
import Text from "@src/lib/ui/Text";
import { Popover, PopoverContent, PopoverTrigger } from "@src/lib/ui/Popover";
import ChevronDownIcon from "@src/lib/icons/12px/chevron-down";
import AgentAvatar from "@src/features/agents/AgentAvatar";
import { ManagerAgent, managerAgentAvatar } from "./managerAgents";

// A manager agent's panel title: its bot and name open "About this agent"
// (what it does), as mobile's info sheet. No Edit or Archive: managers
// can't manage agents.
const ManagerAgentPanelHeaderDetails: React.FC<{ agent: ManagerAgent }> = ({
  agent,
}) => {
  const avatar = managerAgentAvatar(agent);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="-ml-1.5 flex min-w-0 items-center gap-2 rounded-lg py-1 pr-2 pl-1.5 transition-colors hover:bg-state-hover focus-visible:ring-3 focus-visible:ring-brand-border focus-visible:outline-none active:bg-state-pressed data-[state=open]:bg-state-hover"
          aria-label={`About ${agent.name}`}
          data-testid="agent-panel-details-trigger"
        >
          <AgentAvatar agent={avatar} size="icon" />
          <Text
            variant="compact-title"
            className="block min-w-0 truncate text-text-primary"
          >
            {agent.name}
          </Text>
          <ChevronDownIcon className="size-3 shrink-0 text-icon-tertiary" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="flex w-85 flex-col gap-4 p-4"
        data-testid="agent-panel-details"
      >
        <div className="flex items-center gap-3">
          <AgentAvatar agent={avatar} size="lg" />
          <div className="flex min-w-0 flex-col">
            <Text
              variant="compact-title"
              className="text-text-primary"
              ellipsis
            >
              {agent.name}
            </Text>
            <Text
              variant="helper-description"
              className="text-text-tertiary"
              multiline
            >
              {agent.summary}
            </Text>
          </div>
        </div>
        <div className="flex flex-col gap-1.5">
          <Text variant="helper-title" className="text-text-secondary">
            What it does
          </Text>
          <ul className="flex list-disc flex-col gap-1 pl-4 text-text-primary">
            {agent.capabilities.map((capability) => (
              <li key={capability}>
                <Text variant="helper-description" multiline>
                  {capability}
                </Text>
              </li>
            ))}
          </ul>
        </div>
        <Text
          variant="helper-description"
          className="text-text-tertiary"
          multiline
        >
          Works within your permissions. Only sees your locations and your team.
        </Text>
      </PopoverContent>
    </Popover>
  );
};

export default ManagerAgentPanelHeaderDetails;
